import React, { useMemo } from 'react'
import { useTheme } from '@material-ui/core/styles'
import { fDateTime } from 'src/utils/formatTime'
import DashboardDisplay from './DashboardDisplay'
import AnalysisDisplay from './AnalysisDisplay'
import FileNameDisplay from './FileNameDisplay'
import RunTypeDisplay from './RunTypeDisplay'
import SampleNameDisplay from './SampleNameDisplay'
import NotesDisplay from './NotesDisplay'
import DeleteFileButton from './DeleteFileButton'
import Tooltip from '@mui/material/Tooltip'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import TonalChip, { FILE_TYPE_TONES } from './TonalChip'
import { refusalSentence } from 'src/utils/importRefusal'

const getTypeInfo = (row) => {
  if (row.file_type === 'fastq' && row.fastq2_filename) {
    return { label: 'FASTQ Pair', tone: FILE_TYPE_TONES.FASTQ, warning: false }
  }
  if (row.file_type === 'fastq') {
    return { label: 'FASTQ File', tone: FILE_TYPE_TONES.FASTQ, warning: true }
  }
  if (row.file_type === 'vcf') {
    return { label: 'VCF', tone: FILE_TYPE_TONES.VCF, warning: false }
  }
  if (row.file_type === 'ont_bam') {
    return { label: 'ONT BAM', tone: FILE_TYPE_TONES.BAM, warning: false }
  }
  if (row.file_type === 'pacbio_bam') {
    return { label: 'PacBio BAM', tone: FILE_TYPE_TONES.BAM, warning: false }
  }
  return { label: 'Unknown', tone: 'neutral', warning: false }
}

// A VCF that did not verify carries `refusal_reason` (a sentence) on a current server.
// It reads the neutral sentence plus that explanation; without the key (an older
// server, or a file that is still verifying) the chip reads as before.
export const vcfRefusalText = (row) =>
  row?.file_type === 'vcf' && row.refusal_reason ? refusalSentence(row.refusal_reason) : null

export const FileTypeBadge = ({ row }) => {
  const theme = useTheme()
  const { label, tone, warning } = getTypeInfo(row)

  // For FASTQ, VCF, and ONT BAM files: check is_processed and is_verified fields
  const isVerifying =
    (row.file_type === 'fastq' ||
      row.file_type === 'vcf' ||
      row.file_type === 'ont_bam' ||
      row.file_type === 'pacbio_bam') &&
    !row.is_processed
  const isUnverified =
    (row.file_type === 'fastq' ||
      row.file_type === 'vcf' ||
      row.file_type === 'ont_bam' ||
      row.file_type === 'pacbio_bam') &&
    row.is_processed &&
    !row.is_verified

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
      <TonalChip tone={tone} label={label} />
      {warning && (
        <Tooltip title="Needs pair" arrow>
          <WarningAmberIcon style={{ color: theme.palette.warning.main, fontSize: 18 }} />
        </Tooltip>
      )}
      {isVerifying && <TonalChip tone="info" label="Verifying" dot />}
      {isUnverified &&
        (vcfRefusalText(row) ? (
          <Tooltip title={vcfRefusalText(row)} arrow>
            <span data-testid="vcf-refusal">
              <TonalChip tone="error" label="Not verified" dot />
            </span>
          </Tooltip>
        ) : (
          <TonalChip tone="error" label="Not verified" dot />
        ))}
    </div>
  )
}

export const useFilesTableColumns = ({
  processedData,
  annotationStatus,
  handleAnnotationModelOpen,
  downloadingFastqId,
  downloadingReportId,
  downloadingVcfId,
  handleDownloadFastq,
  handleDownloadReport,
  handleDownloadRawVcf,
  handleOpenAIDialog,
  setFileNotes,
  setFinishedInfo,
  handleFileDelete,
  optimisticUpdates,
  downloadingBamId,
  handleDownloadOntBam,
  handleDownloadPacBioBam,
}) => {
  return useMemo(
    () => [
      {
        name: 'dashboard',
        label: 'Dashboard',
        options: {
          setCellProps: () => ({
            style: {
              minWidth: '150px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
          }),
          filter: false,
          sort: false,
          customBodyRenderLite: (dataIndex) => {
            const row = processedData[dataIndex]
            return <DashboardDisplay row={row} />
          },
        },
      },
      {
        name: 'analysis',
        label: 'Analysis',
        options: {
          filter: false,
          sort: false,
          customBodyRenderLite: (dataIndex) => {
            const row = processedData[dataIndex]
            return (
              <AnalysisDisplay row={row} annotationStatus={annotationStatus} onAnnotate={handleAnnotationModelOpen} />
            )
          },
        },
      },
      {
        name: 'name',
        label: 'Filename2',
        options: {
          filter: true,
          sort: true,
          customBodyRenderLite: (dataIndex) => {
            const row = processedData[dataIndex]
            return (
              <FileNameDisplay
                row={row}
                downloadingFastqId={downloadingFastqId}
                downloadingReportId={downloadingReportId}
                downloadingVcfId={downloadingVcfId}
                onDownloadFastq={() => handleDownloadFastq(row.fastq_pair_id, row.sample_name)}
                onDownloadReport={handleDownloadReport}
                onDownloadRawVcf={handleDownloadRawVcf}
                onOpenAIDialog={handleOpenAIDialog}
                downloadingBamId={downloadingBamId}
                onDownloadOntBam={handleDownloadOntBam}
                onDownloadPacBioBam={handleDownloadPacBioBam}
              />
            )
          },
        },
      },
      {
        name: 'run_type',
        label: 'Run Type',
        options: {
          filter: true,
          sort: true,
          customBodyRenderLite: (dataIndex) => {
            const row = processedData[dataIndex]
            return <RunTypeDisplay row={row} />
          },
        },
      },
      {
        name: 'sample_name',
        label: 'Sample',
        options: {
          filter: true,
          sort: true,
          customBodyRenderLite: (dataIndex) => {
            const row = processedData[dataIndex]
            return <SampleNameDisplay row={row} />
          },
        },
      },
      {
        name: 'notes',
        label: 'Notes',
        options: {
          filter: false,
          sort: false,
          customBodyRenderLite: (dataIndex) => {
            const row = processedData[dataIndex]
            return (
              <NotesDisplay
                row={row}
                onSaveNotes={async (notes) => await setFileNotes(row, notes)}
                onFinishedChange={() => setFinishedInfo(row)}
              />
            )
          },
        },
      },
      {
        name: 'created_at',
        label: 'Uploaded At',
        options: {
          filter: false,
          sort: true,
          customBodyRenderLite: (dataIndex) => {
            const row = processedData[dataIndex]
            return row && row.created_at ? fDateTime(row.created_at) : null
          },
        },
      },
      {
        name: 'delete',
        label: 'Delete',
        options: {
          filter: false,
          sort: false,
          customBodyRenderLite: (dataIndex) => {
            const row = processedData[dataIndex]
            if (!row) return null

            const fileId =
              row.vcf_id || row.fastq_pair_id || row.fastq_file_id || row.ont_bam_id || row.pacbio_bam_id || row.id
            const isDeleting = optimisticUpdates?.deletingFileIds?.includes?.(fileId) || false

            return <DeleteFileButton onClickConfirm={() => handleFileDelete(row)} disabled={isDeleting} />
          },
        },
      },
    ],
    [
      processedData,
      annotationStatus,
      handleAnnotationModelOpen,
      downloadingFastqId,
      downloadingReportId,
      downloadingVcfId,
      handleDownloadFastq,
      handleDownloadReport,
      handleDownloadRawVcf,
      handleOpenAIDialog,
      setFileNotes,
      setFinishedInfo,
      handleFileDelete,
      optimisticUpdates,
      downloadingBamId,
      handleDownloadOntBam,
      handleDownloadPacBioBam,
    ],
  )
}

// Legacy function for backward compatibility - now just returns the columns array directly
export const createFilesTableColumns = ({
  processedData,
  annotationStatus,
  handleAnnotationModelOpen,
  downloadingFastqId,
  downloadingReportId,
  downloadingVcfId,
  handleDownloadFastq,
  handleDownloadReport,
  handleDownloadRawVcf,
  handleOpenAIDialog,
  setFileNotes,
  setFinishedInfo,
  handleFileDelete,
  optimisticUpdates,
  downloadingBamId,
  handleDownloadOntBam,
  handleDownloadPacBioBam,
}) => [
  {
    name: 'type',
    label: 'Type',
    options: {
      filter: true,
      filterType: 'dropdown',
      filterOptions: {
        names: ['VCF', 'FASTQ Pair', 'FASTQ File', 'ONT BAM', 'PacBio BAM'],
      },
      sort: false,
      customBodyRenderLite: (dataIndex) => {
        const row = processedData[dataIndex]
        return <FileTypeBadge row={row} />
      },
    },
  },
  // {
  //   name: 'dashboard',
  //   label: 'Dashboard',
  //   options: {
  //     setCellProps: () => ({
  //       style: {
  //         minWidth: '150px',
  //         whiteSpace: 'nowrap',
  //         overflow: 'hidden',
  //         textOverflow: 'ellipsis',
  //       },
  //     }),
  //     filter: false,
  //     sort: false,
  //     customBodyRenderLite: (dataIndex) => {
  //       const row = processedData[dataIndex]
  //       return <DashboardDisplay row={row} />
  //     },
  //   },
  // },
  // {
  //   name: 'analysis',
  //   label: 'Analysis',
  //   options: {
  //     filter: false,
  //     sort: false,
  //     customBodyRenderLite: (dataIndex) => {
  //       const row = processedData[dataIndex]
  //       return <AnalysisDisplay row={row} annotationStatus={annotationStatus} onAnnotate={handleAnnotationModelOpen} />
  //     },
  //   },
  // },
  {
    name: 'name',
    label: 'Filename',
    options: {
      filter: true,
      sort: false,
      customBodyRenderLite: (dataIndex) => {
        const row = processedData[dataIndex]
        return (
          <FileNameDisplay
            row={row}
            downloadingFastqId={downloadingFastqId}
            downloadingReportId={downloadingReportId}
            downloadingVcfId={downloadingVcfId}
            onDownloadFastq={() => handleDownloadFastq(row.fastq_pair_id, row.sample_name)}
            onDownloadReport={handleDownloadReport}
            onDownloadRawVcf={handleDownloadRawVcf}
            onOpenAIDialog={handleOpenAIDialog}
            downloadingBamId={downloadingBamId}
            onDownloadOntBam={handleDownloadOntBam}
            onDownloadPacBioBam={handleDownloadPacBioBam}
          />
        )
      },
    },
  },
  // {
  //   name: 'run_type',
  //   label: 'Run Type',
  //   options: {
  //     filter: true,
  //     sort: true,
  //     customBodyRenderLite: (dataIndex) => {
  //       const row = processedData[dataIndex]
  //       return <RunTypeDisplay row={row} />
  //     },
  //   },
  // },
  {
    name: 'sample_name',
    label: 'Sample',
    options: {
      filter: true,
      sort: false,
      customBodyRenderLite: (dataIndex) => {
        const row = processedData[dataIndex]
        return <SampleNameDisplay row={row} />
      },
    },
  },
  // {
  //   name: 'notes',
  //   label: 'Notes',
  //   options: {
  //     filter: false,
  //     sort: false,
  //     customBodyRenderLite: (dataIndex) => {
  //       const row = processedData[dataIndex]
  //       return (
  //         <NotesDisplay
  //           row={row}
  //           onSaveNotes={async (notes) => await setFileNotes(row, notes)}
  //           onFinishedChange={() => setFinishedInfo(row)}
  //         />
  //       )
  //     },
  //   },
  // },
  {
    name: 'created_at',
    label: 'Uploaded At',
    options: {
      filter: false,
      sort: true,
      customBodyRenderLite: (dataIndex) => {
        const row = processedData[dataIndex]
        return row && row.uploaded_at ? fDateTime(row.uploaded_at) : null
      },
    },
  },
  {
    name: 'delete',
    label: 'Delete',
    options: {
      filter: false,
      sort: false,
      customBodyRenderLite: (dataIndex) => {
        const row = processedData[dataIndex]
        if (!row) return null

        const fileId =
          row.vcf_id || row.fastq_pair_id || row.fastq_file_id || row.ont_bam_id || row.pacbio_bam_id || row.id
        const isDeleting = optimisticUpdates?.deletingFileIds?.includes?.(fileId) || false

        return <DeleteFileButton onClickConfirm={() => handleFileDelete(row)} disabled={isDeleting} />
      },
    },
  },
]
